import clsx from "clsx";
import get from "lodash/get";
import React, { useEffect, useState } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FieldError, useForm } from "react-hook-form/dist/index.ie11";
import { useTranslation } from "react-i18next";

import Button from "~/shared/ui/Fields/Button";
import DatesPicker from "~/shared/ui/Fields/DatePicker";
import MultiSelect from "~/shared/ui/Fields/MultiSelect";
import Select from "~/shared/ui/Fields/Select";
import { onScrollBottomDealers } from "~/hooks/selectDealerValue";
import { language } from "~/libs/getData";
import { defaultStaffFilterValues } from "~/pages/Staff";
import {
  FILTER_BY,
  filterByArr,
  itemsDataRoute,
  resolveDropList,
} from "~/pages/Staff/assets/StaffFilter/lib";
import {
  DealersLocationItem,
  DealerStatus,
  useGetDealersLocationItemQuery,
} from "~/schemas/generated/graphql";
import { filterItem } from "~/shared/lib/filterHelpers";
import { stringListToSelectOption } from "~/shared/lib/StringListToSelectOption/stringListToSelectOption";

import s from "./s.less";

export interface IListItem {
  label: string;
  value: string | number | FILTER_BY;
}

interface IListItemFilterBy {
  label: string;
  value: FILTER_BY;
}

export interface IEmployeeFilter {
  filterBy?: IListItemFilterBy;
  filterItems?: IListItem[];
  period?: Date;
}

interface IStaffFilterProps {
  toggleFilter: () => void;
  filterData: Partial<IEmployeeFilter>;
  setFilterData: React.Dispatch<IEmployeeFilter>;
  isEmployee?: boolean | undefined;
  clickBtn?: () => void;
}

const controlLocationItem = {
  city: undefined,
  FO: undefined,
  subject: undefined,
  country: undefined,
};

const StaffFilter = ({
  filterData,
  setFilterData,
  toggleFilter,
  isEmployee = false,
  clickBtn,
}: IStaffFilterProps) => {
  const { t } = useTranslation();
  const isEn = language === "en";
  const [manualSelect, setManualSelect] = useState(false);
  const schema = yup.object().shape({
    filterItems: yup.array().min(1, t("validation.required")),
  });

  const { control, handleSubmit, watch, setValue, reset, errors } =
    useForm<IEmployeeFilter>({
      defaultValues: {
        ...defaultStaffFilterValues(),
        ...filterData,
      },
      resolver: yupResolver(schema),
    });
  const watchFilterBy = watch("filterBy");

  const resetForm = () => {
    reset(defaultStaffFilterValues());
    setFilterData(defaultStaffFilterValues());
  };

  //request data
  const dealerCity = useGetDealersLocationItemQuery({
    variables: {
      item: DealersLocationItem["City"],
      ...filterItem(controlLocationItem, (elem) => elem !== "city"),
    },
  });
  const dealerCountry = useGetDealersLocationItemQuery({
    variables: {
      item: DealersLocationItem["Country"],
      ...filterItem(controlLocationItem, (elem) => elem !== "country"),
    },
  });
  const dealerSubject = useGetDealersLocationItemQuery({
    variables: {
      item: DealersLocationItem["Subject"],
      ...filterItem(controlLocationItem, (elem) => elem !== "subject"),
    },
  });
  const dealerFo = useGetDealersLocationItemQuery({
    variables: {
      item: DealersLocationItem["Fo"],
      ...filterItem(controlLocationItem, (elem) => elem !== "FO"),
    },
  });
  const activeSearch = DealerStatus.Active;
  const dealersScrolledInstance = onScrollBottomDealers(activeSearch);

  const listOfDrops = {
    [FILTER_BY.CITY]: get(dealerCity, itemsDataRoute(isEn), []).map(
      stringListToSelectOption
    ),
    [FILTER_BY.FO]: get(dealerFo, itemsDataRoute(isEn), []).map(
      stringListToSelectOption
    ),
    [FILTER_BY.AREA]: get(dealerSubject, itemsDataRoute(isEn), []).map(
      stringListToSelectOption
    ),
    [FILTER_BY.COUNTRY]: get(dealerCountry, itemsDataRoute(isEn), []).map(
      stringListToSelectOption
    ),
    [FILTER_BY.DEALER]: get(dealersScrolledInstance, "dealersForSelect", []),
  };

  const onSubmit = (data) => {
    setFilterData(data);
    toggleFilter();
    clickBtn();
  };

  useEffect(() => {
    listOfDrops[FILTER_BY.DEALER] = get(
      dealersScrolledInstance,
      "dealersForSelect",
      []
    );
  }, [dealersScrolledInstance.GetViolationTypesLoading]);

  useEffect(() => {
    manualSelect && setValue("filterItems", []);
  }, [watchFilterBy]);

  return (
    <div className={s.container}>
      <form onSubmit={handleSubmit(onSubmit)} className={s.form}>
        <ul className={s.filterControls}>
          <li>
            <label htmlFor="staff_select_property">
              {t("staff.filter_by")}
            </label>
            <Select
              id={"staff_select_property"}
              name={"filterBy"}
              optionObjects={filterByArr.map((el) => ({
                value: el.value,
                label: isEn ? el.labelEn : el.label,
              }))}
              control={control}
              customClassName={s.select}
              onChangeDecorator={() => setManualSelect(true)}
              disabled={isEmployee}
            />
          </li>
          <li>
            <label htmlFor="staff_select_property">
              *{t("staff.filter_by_values")}
            </label>
            <MultiSelect
              name={"filterItems"}
              label={""}
              control={control}
              options={resolveDropList(watchFilterBy.value, listOfDrops)}
              onScrollBottom={
                watchFilterBy.value === FILTER_BY.DEALER
                  ? dealersScrolledInstance.introspectionHandler
                  : () => {}
              }
              customInput={{ height: 13, marginTop: -1 }}
              customLabelClass={s.customLabelClass}
              getSearchText={
                watchFilterBy.value === FILTER_BY.DEALER
                  ? dealersScrolledInstance.searchDealer
                  : () => {}
              }
              loading={
                watchFilterBy.value === FILTER_BY.DEALER
                  ? dealersScrolledInstance.GetViolationTypesLoading
                  : false
              }
              isDisabled={isEmployee}
              error={(errors["filterItems"] as unknown as FieldError)?.message}
            />
          </li>
          <li>
            <label htmlFor="staff_select_property">{t("staff.period")}</label>
            <DatesPicker
              name={"period"}
              control={control}
              gray
              customClassName={clsx(s.date_q)}
              dateFormat={"dd.MM.yyyy"}
              defaultValue={new Date()}
              showFullMonthYearPicker
              disabled={isEmployee}
            />
          </li>
        </ul>
        {!isEmployee && (
          <div className={s.controlWrapper}>
            <Button
              customClassName={clsx(s.btn, s.btn_white)}
              txtBtn={t("ui.filter_reset")}
              onClick={resetForm}
            />
            <Button
              customClassName={s.btn}
              isForm
              txtBtn={t("ui.filter_show")}
            />
          </div>
        )}
      </form>
    </div>
  );
};

export default StaffFilter;
